import React from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import LoadingSpinner from "./core/LoadingSpinner";

/**
 * PublicRoute - Inverse of PrivateRoute
 *
 * Used for auth pages (Login, Signup) and the root route.
 * Prevents authenticated users from seeing auth pages by redirecting
 * them to /schedule before the page renders.
 *
 * Flow:
 * 1. isLoading = true  → Show loading spinner (auth check in progress)
 * 2. isAuthenticated   → Redirect to /schedule (user is logged in)
 * 3. Not authenticated → Render children (show Login/Signup page)
 *
 * This eliminates the "login page flash" that occurred when authenticated
 * users visited / or /login — previously the Login component would render
 * first, then its useEffect would redirect after the first paint.
 */
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  // Auth check still in progress — show spinner instead of flashing Login
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" color="primary" />
      </div>
    );
  }

  // Already authenticated — redirect to admin dashboard
  if (isAuthenticated) {
    return <Navigate to="/schedule" replace />;
  }

  // Not authenticated — show the auth page (Login / Signup)
  return <>{children}</>;
};

export default PublicRoute;
